import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import type { DecisionFilters } from '@/services/types';

/**
 * Filter controls. Every option list comes from the API, never from a literal
 * in this file -- a hardcoded outcome vocabulary silently drops any code a later
 * extraction pass adds, and the screen looks complete while hiding rows.
 */
export function DecisionFilterBar({
  filters,
  outcomeCodes,
  authorities,
  onChange,
}: {
  filters: DecisionFilters;
  outcomeCodes: string[];
  authorities: string[];
  onChange: (next: DecisionFilters) => void;
}) {
  const { t } = useTranslation(['anatel', 'common']);
  const selected = filters.outcome_codes ?? [];

  function toggleOutcome(code: string) {
    const next = selected.includes(code)
      ? selected.filter((item) => item !== code)
      : [...selected, code];
    onChange({ ...filters, outcome_codes: next, offset: 0 });
  }

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="space-y-1 sm:col-span-2">
          <span className="text-xs font-medium text-foreground">
            {t('anatel:decisions.filters.search')}
          </span>
          <input
            type="search"
            value={filters.query ?? ''}
            placeholder={t('anatel:decisions.filters.searchPlaceholder')}
            onChange={(event) =>
              onChange({ ...filters, query: event.target.value || null, offset: 0 })
            }
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-foreground">
            {t('anatel:decisions.filters.yearFrom')}
          </span>
          <input
            type="number"
            inputMode="numeric"
            value={filters.year_from ?? ''}
            onChange={(event) =>
              onChange({
                ...filters,
                year_from: event.target.value ? Number(event.target.value) : null,
                offset: 0,
              })
            }
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>

        <label className="space-y-1 sm:col-span-2">
          <span className="text-xs font-medium text-foreground">
            {t('anatel:decisions.filters.authority')}
          </span>
          <select
            value={filters.deciding_authority ?? ''}
            onChange={(event) =>
              onChange({
                ...filters,
                deciding_authority: event.target.value || null,
                offset: 0,
              })
            }
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="">{t('anatel:decisions.filters.authorityAny')}</option>
            {authorities.map((authority) => (
              <option key={authority} value={authority}>
                {authority}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-foreground">
            {t('anatel:decisions.filters.minFine')}
          </span>
          <input
            type="number"
            inputMode="decimal"
            value={filters.min_fine ?? ''}
            onChange={(event) =>
              onChange({
                ...filters,
                min_fine: event.target.value ? Number(event.target.value) : null,
                offset: 0,
              })
            }
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>
      </div>

      <fieldset className="space-y-1.5">
        <legend className="text-xs font-medium text-foreground">
          {t('anatel:decisions.filters.outcomes')}
        </legend>
        <div className="flex flex-wrap gap-1.5">
          {outcomeCodes.map((code) => {
            const active = selected.includes(code);
            return (
              <button
                key={code}
                type="button"
                aria-pressed={active}
                onClick={() => toggleOutcome(code)}
                className={cn(
                  'rounded border px-2 py-1 text-xs transition-colors',
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {t(`anatel:overview.outcomes.${code}`, { defaultValue: code })}
              </button>
            );
          })}
        </div>
      </fieldset>

      <label className="flex items-center gap-2 text-xs text-foreground">
        <input
          type="checkbox"
          checked={filters.only_with_reasoning ?? false}
          onChange={(event) =>
            onChange({ ...filters, only_with_reasoning: event.target.checked, offset: 0 })
          }
        />
        {t('anatel:decisions.filters.onlyWithReasoning')}
      </label>
    </div>
  );
}
